import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, ChevronDown, ChevronUp, ArrowLeft, Settings, Play, CheckCircle, Users } from "lucide-react";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import type { Tables } from "@/integrations/supabase/types";

type Season = Tables<"seasons">;

const STATUS_LABEL_KEYS: Record<string, string> = {
  draft: "seasons.statusDraft",
  active: "seasons.statusActive",
  completed: "seasons.statusCompleted",
  archived: "seasons.statusArchived",
};

const STATUS_VARIANTS: Record<string, "secondary" | "default" | "outline" | "destructive"> = {
  draft: "secondary",
  active: "default",
  completed: "outline",
  archived: "secondary",
};

const seasonSchema = z.object({
  name: z.string().min(1),
  start_date: z.string().min(1),
  end_date: z.string().min(1),
  status: z.enum(["draft", "active", "completed", "archived"]),
}).refine((d) => d.end_date > d.start_date, {
  message: "End date must be after start date",
  path: ["end_date"],
});

type SeasonFormValues = z.infer<typeof seasonSchema>;

export default function AdminSeasons({ onNavigateToGroups }: { onNavigateToGroups?: (seasonId: string) => void } = {}) {
  const { t } = useTranslation("admin");
  const queryClient = useQueryClient();
  const { organization } = useOrganization();
  const [statusFilter, setStatusFilter] = useState("all");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editingSeason, setEditingSeason] = useState<Season | null>(null);
  const [viewMode, setViewMode] = useState<"list" | "form">("list");

  const { data: seasons = [], isLoading } = useQuery({
    queryKey: ["admin-seasons", organization?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("seasons")
        .select("*")
        .eq("organization_id", organization!.id)
        .order("start_date", { ascending: false });
      if (error) throw error;
      return data as Season[];
    },
    enabled: !!organization?.id,
  });

  const saveMutation = useMutation({
    mutationFn: async (values: SeasonFormValues) => {
      if (editingSeason) {
        const { error } = await supabase
          .from("seasons")
          .update(values)
          .eq("id", editingSeason.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("seasons").insert([{
          name: values.name,
          start_date: values.start_date,
          end_date: values.end_date,
          status: values.status,
          organization_id: organization!.id,
        }]);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-seasons"] });
      setViewMode("list");
      setEditingSeason(null);
      toast.success(editingSeason ? t("seasons.seasonUpdated") : t("seasons.seasonCreated"));
    },
    onError: (err: Error) => { console.error(err); toast.error(err?.message || t("toast.couldNotSaveSeason")); },
  });

  const statusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase.from("seasons").update({ status }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-seasons"] });
      toast.success(t("admin:toast.seasonStatusUpdated"));
    },
    onError: (err: Error) => { console.error(err); toast.error(err?.message || t("toast.couldNotUpdateSeasonStatus")); },
  });

  const filtered = statusFilter === "all" ? seasons : seasons.filter((s) => s.status === statusFilter);

  if (viewMode === "form") {
    return (
      <SeasonForm
        season={editingSeason}
        onBack={() => { setViewMode("list"); setEditingSeason(null); }}
        onSubmit={(values) => saveMutation.mutate(values)}
        isSubmitting={saveMutation.isPending}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder={t("seasons.filterStatus")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("seasons.all")}</SelectItem>
            <SelectItem value="draft">{t("seasons.statusDraft")}</SelectItem>
            <SelectItem value="active">{t("seasons.statusActive")}</SelectItem>
            <SelectItem value="completed">{t("seasons.statusCompleted")}</SelectItem>
            <SelectItem value="archived">{t("seasons.statusArchived")}</SelectItem>
          </SelectContent>
        </Select>
        <Button size="sm" onClick={() => { setEditingSeason(null); setViewMode("form"); }} className="gap-1">
          <Plus className="h-4 w-4" /> {t("seasons.createSeason")}
        </Button>
      </div>

      {isLoading ? (
        <p className="text-muted-foreground text-sm">{t("seasons.loading")}</p>
      ) : filtered.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t("seasons.noSeasonsFound")}</p>
      ) : (
        filtered.map((season) => {
          const expanded = expandedId === season.id;
          return (
            <Card key={season.id}>
              <CardHeader
                className="cursor-pointer py-3"
                onClick={() => setExpandedId(expanded ? null : season.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CardTitle className="text-base">{season.name}</CardTitle>
                    <Badge variant={STATUS_VARIANTS[season.status] ?? "secondary"}>
                      {t(STATUS_LABEL_KEYS[season.status]) ?? season.status}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span>{season.start_date} — {season.end_date}</span>
                    {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </div>
                </div>
              </CardHeader>
              {expanded && (
                <CardContent className="pt-0 space-y-3">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-muted-foreground">{t("seasons.startDate")}:</span> {season.start_date}
                    </div>
                    <div>
                      <span className="text-muted-foreground">{t("seasons.endDate")}:</span> {season.end_date}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setEditingSeason(season); setViewMode("form"); }}
                      className="gap-1"
                    >
                      <Settings className="h-3.5 w-3.5" /> {t("seasons.edit")}
                    </Button>
                    {onNavigateToGroups && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onNavigateToGroups(season.id)}
                        className="gap-1"
                      >
                        <Users className="h-3.5 w-3.5" /> {t("seasons.groups.title")}
                      </Button>
                    )}
                    {season.status === "draft" && (
                      <Button
                        size="sm"
                        onClick={() => statusMutation.mutate({ id: season.id, status: "active" })}
                        className="gap-1"
                      >
                        <Play className="h-3.5 w-3.5" /> {t("seasons.activate")}
                      </Button>
                    )}
                    {season.status === "active" && (
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => statusMutation.mutate({ id: season.id, status: "completed" })}
                        className="gap-1"
                      >
                        <CheckCircle className="h-3.5 w-3.5" /> {t("seasons.endSeason")}
                      </Button>
                    )}
                  </div>
                </CardContent>
              )}
            </Card>
          );
        })
      )}
    </div>
  );
}

function SeasonForm({
  season,
  onBack,
  onSubmit,
  isSubmitting,
}: {
  season: Season | null;
  onBack: () => void;
  onSubmit: (values: SeasonFormValues) => void;
  isSubmitting: boolean;
}) {
  const { t } = useTranslation("admin");
  const form = useForm<SeasonFormValues>({
    resolver: zodResolver(seasonSchema),
    defaultValues: {
      name: season?.name ?? "",
      start_date: season?.start_date ?? "",
      end_date: season?.end_date ?? "",
      status: (season?.status as SeasonFormValues["status"]) ?? "draft",
    },
  });

  return (
    <div>
      <Button variant="ghost" size="sm" onClick={onBack} className="mb-4 gap-1">
        <ArrowLeft className="h-4 w-4" /> {t("seasons.backToSeasons")}
      </Button>
      <h2 className="text-lg font-semibold mb-4">
        {season ? t("seasons.editSeason") : t("seasons.createSeason")}
      </h2>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 max-w-md">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("seasons.name")}</FormLabel>
                <FormControl>
                  <Input placeholder={t("seasons.namePlaceholder")} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="start_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("seasons.startDate")}</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="end_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("seasons.endDate")}</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="status"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("seasons.status")}</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="draft">{t("seasons.statusDraft")}</SelectItem>
                    <SelectItem value="active">{t("seasons.statusActive")}</SelectItem>
                    <SelectItem value="completed">{t("seasons.statusCompleted")}</SelectItem>
                    <SelectItem value="archived">{t("seasons.statusArchived")}</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? t("seasons.saving") : season ? t("seasons.update") : t("seasons.createSeason")}
          </Button>
        </form>
      </Form>
    </div>
  );
}
